import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

export type NotificationChannel = "SMS" | "EMAIL" | "PUSH";
export type NotificationEventType =
  | "ARRIVING_SOON"
  | "OUT_FOR_PICKUP"
  | "PICKED_UP"
  | "PICKUP_FAILED"
  | "OUT_FOR_DELIVERY"
  | "DELIVERED"
  | "DELIVERY_FAILED"
  | "OUT_FOR_RETURN"
  | "RETURNED"
  | "RETURN_FAILED"
  | "IN_TRANSIT"
  | "HANDED_TO_CARRIER"
  | "DISPATCHED"
  | "ORDER_CONFIRMED"
  | "LABEL_READY";
export type NotificationStatus = "PENDING" | "SENT" | "FAILED" | "SKIPPED";
export type NotificationRecipientType = "SENDER" | "RECIPIENT";

export interface NotificationLog {
  id: string;
  shipmentId: string;
  taskId: string | null;
  eventType: NotificationEventType;
  channel: NotificationChannel;
  recipientType: NotificationRecipientType;
  recipientValue: string;
  status: NotificationStatus;
  errorMessage: string | null;
  templateId: string;
  messagePreview: string | null;
  createdAt: string;
  sentAt: string | null;
  shipment: { shipmentNumber: number } | null;
}

interface NotificationLogsResponse {
  success: boolean;
  data: NotificationLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface NotificationLogsFilters {
  page?: number;
  limit?: number;
  shipmentNumber?: number;
  channel?: NotificationChannel;
  eventType?: NotificationEventType;
  status?: NotificationStatus;
  recipientType?: NotificationRecipientType;
  startDate?: string;
  endDate?: string;
}

interface NotificationStatsResponse {
  success: boolean;
  data: {
    byStatus: Record<string, number>;
    byChannel: Record<string, number>;
    byEventType: Record<string, number>;
    recent24h: number;
  };
}

export const useNotificationLogs = (filters: NotificationLogsFilters = {}) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["notification-logs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.page) params.set("page", String(filters.page));
      if (filters.limit) params.set("limit", String(filters.limit));
      if (filters.shipmentNumber)
        params.set("shipmentNumber", String(filters.shipmentNumber));
      if (filters.channel) params.set("channel", filters.channel);
      if (filters.eventType) params.set("eventType", filters.eventType);
      if (filters.status) params.set("status", filters.status);
      if (filters.recipientType)
        params.set("recipientType", filters.recipientType);
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);

      const response = await axios.get<NotificationLogsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/notification-logs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    enabled: !!token,
  });
};

export const useNotificationStats = () => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["notification-stats"],
    queryFn: async () => {
      const response = await axios.get<NotificationStatsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/notification-logs/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    },
    enabled: !!token,
  });
};
